import React from 'react';
import './Landing.css';

interface LandingProps {
  onSignIn: () => void;
  onSignUp: () => void;
}

export const Landing: React.FC<LandingProps> = ({ onSignIn, onSignUp }) => {
  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="logo">
            <span className="logo-icon">📸</span>
            <span className="logo-text">Photosynthesize</span>
          </div>
          <div className="nav-actions">
            <button onClick={onSignIn} className="btn-text">
              Sign In
            </button>
            <button onClick={onSignUp} className="btn-primary">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-container">
          <h1 className="hero-title">
            AI-Powered Photo Management for Professional Photographers
          </h1>
          <p className="hero-subtitle">
            Direct upload from your Canon R6 Mark II. Burst AI automatically selects your best shots from 40fps sequences.
            Smart storage that keeps what matters, archives the rest.
          </p>
          <div className="hero-actions">
            <button onClick={onSignUp} className="btn-cta">
              Start Free Trial
            </button>
            <button className="btn-secondary">
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features">
        <div className="features-container">
          <h2 className="section-title">
            Everything you need, nothing you don't
          </h2>

          <div className="features-grid">
            <FeatureCard
              icon="📸"
              title="Canon R6 Mark II Direct Upload"
              description="SFTP from your camera to cloud. No cables, no computer required. Automatic sync."
            />
            <FeatureCard
              icon="🤖"
              title="Burst AI Selection"
              description="Shooting at 40fps? Our AI analyzes every shot and keeps your best 5, archives the rest. 77% storage savings."
            />
            <FeatureCard
              icon="💾"
              title="Intelligent Storage Tiers"
              description="HOT storage for your best work. WARM for alternates. COLD archive for everything else. Deep Archive after 90 days."
            />
            <FeatureCard
              icon="🔍"
              title="OpenCV Quality Analysis"
              description="Automatic sharpness, contrast, and brightness scoring. Never miss the perfect shot in a burst sequence."
            />
            <FeatureCard
              icon="📊"
              title="Smart Retention Policies"
              description="Favorites kept forever. Best shots forever. Good alternates for 12 months. Archive for 6 months."
            />
            <FeatureCard
              icon="🔐"
              title="No Lock-In"
              description="Your photos in your S3 bucket. Export anytime. Download before auto-archival. You're in control."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing">
        <div className="pricing-container">
          <h2 className="section-title">
            Simple, transparent pricing
          </h2>

          <div className="pricing-grid">
            <PricingCard
              name="Free"
              price="$0"
              features={[
                '250 photos/month',
                '10 GB storage',
                '90-day retention',
                'Community support',
                'Basic EXIF search'
              ]}
              onSelect={onSignUp}
            />
            <PricingCard
              name="Hobby"
              price="$19.99"
              features={[
                '2,500 photos/month',
                '200 GB storage',
                '12-month retention',
                'Email support',
                'Overage billing available'
              ]}
              onSelect={onSignUp}
            />
            <PricingCard
              name="Pro"
              price="$49.99"
              featured
              features={[
                '10,000 photos/month',
                '1 TB storage',
                'Smart retention (best 20% forever)',
                'Burst AI enabled',
                'Priority processing',
                'API access'
              ]}
              onSelect={onSignUp}
            />
            <PricingCard
              name="Studio"
              price="$99.99"
              features={[
                '30,000 photos/month',
                '3 TB storage',
                'Keep everything forever',
                'Premium Burst AI',
                'Phone support',
                'Team sharing'
              ]}
              onSelect={onSignUp}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="cta-container">
          <h2 className="cta-title">
            Start organizing your photos today
          </h2>
          <p className="cta-subtitle">
            Start with 250 photos/month free. No credit card required.
          </p>
          <button onClick={onSignUp} className="btn-cta">
            Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <p>&copy; 2025 Photosynthesize. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="feature-card">
      <div className="feature-icon">{icon}</div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description">{description}</p>
    </div>
  );
};

interface PricingCardProps {
  name: string;
  price: string;
  features: string[];
  featured?: boolean;
  onSelect: () => void;
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  price,
  features,
  featured = false,
  onSelect
}) => {
  return (
    <div className={`pricing-card ${featured ? 'pricing-card-featured' : ''}`}>
      <h3 className="pricing-name">{name}</h3>
      <div className="pricing-price">
        {price}<span className="pricing-period">/month</span>
      </div>
      <ul className="pricing-features">
        {features.map((feature, i) => (
          <li key={i} className="pricing-feature">
            <span className="pricing-feature-icon">✓</span>
            {feature}
          </li>
        ))}
      </ul>
      <button onClick={onSelect} className={featured ? 'btn-cta' : 'btn-primary'}>
        Start Free Trial
      </button>
    </div>
  );
};

export default Landing;
